import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, ArrowLeft, Check, AlertTriangle, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClaimAppPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [app, setApp] = useState(null);
  const [form, setForm] = useState({ email: "", relationship: "owner", note: "" });
  const [state, setState] = useState({ submitting: false, result: null, error: "" });

  useEffect(() => {
    document.title = isNl ? `Claim ${slug} — HorseAppCompare` : `Claim ${slug} — HorseAppCompare`;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/apps/${slug}`);
        setApp(data);
      } catch (e) { setApp(false); }
    })();
  }, [slug, isNl]);

  const submit = async (e) => {
    e.preventDefault();
    setState({ submitting: true, result: null, error: "" });
    try {
      const { data } = await axios.post(`${API}/apps/${slug}/claim`, { ...form, language: lang });
      setState({ submitting: false, result: data, error: "" });
    } catch (err) {
      setState({ submitting: false, result: null, error: err?.response?.data?.detail || (isNl ? "Er ging iets mis." : "Something went wrong.") });
    }
  };

  if (app === false) return <main className="hac-container py-20 text-center text-[#94A3B8]">App not found</main>;
  if (!app) return <main className="hac-container py-20 text-center"><Loader2 className="animate-spin mx-auto" /></main>;

  if (state.result) {
    const auto = state.result.domain_match;
    return (
      <main className="hac-container py-20 max-w-2xl" data-testid="claim-success">
        <div className={`rounded-2xl p-8 ${auto ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}>
          {auto ? <Check size={40} className="text-emerald-600 mb-4" /> : <AlertTriangle size={40} className="text-amber-600 mb-4" />}
          <h1 className="font-heading text-3xl text-[#0F172A] mb-2">
            {auto ? (isNl ? "✅ Check je inbox!" : "✅ Check your inbox!") : (isNl ? "Aanvraag ingediend" : "Request submitted")}
          </h1>
          <p className="text-[#64748B] leading-relaxed mb-4">
            {auto
              ? (isNl ? `We hebben een bevestigingsmail gestuurd naar ${form.email}. Klik op de link in de mail om je claim voor ${app.name} af te ronden.` : `We sent a verification email to ${form.email}. Click the link in the email to finalize your claim for ${app.name}.`)
              : (isNl ? `Je e-mail komt niet overeen met het domein van ${app.name} (${app.website}). Onze admin reviewt je aanvraag binnen 2 werkdagen handmatig.` : `Your email doesn't match the domain of ${app.name} (${app.website}). Our admin will manually review your request within 2 business days.`)}
          </p>
          <Link to={`/apps/${slug}`} className="text-[#2563EB] hover:underline text-sm">{isNl ? "← Terug naar de app" : "← Back to the app"}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="hac-container py-12 max-w-2xl" data-testid="claim-page">
      <Link to={`/apps/${slug}`} className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#0F172A] mb-6"><ArrowLeft size={12} /> {isNl ? "Terug" : "Back"}</Link>
      <div className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
        <ShieldCheck size={12} /> {isNl ? "Eigenaar claim" : "Owner claim"}
      </div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0F172A] mb-3">{isNl ? `Claim ${app.name}` : `Claim ${app.name}`}</h1>
      <p className="text-[#64748B] mb-8 leading-relaxed">{isNl
        ? `Ben jij eigenaar van of werk je bij ${app.name}? Claim deze listing om de beschrijving, screenshots en links zelf bij te werken — en krijg een geverifieerd badge dat je listing meer vertrouwen geeft.`
        : `Are you the owner or employee of ${app.name}? Claim this listing to update the description, screenshots and links yourself — and get a verified badge that gives your listing more trust.`}</p>

      <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5">
        <div>
          <label className="overline block mb-1 text-[#64748B]">{isNl ? "Werk-e-mail" : "Work email"} *</label>
          <input type="email" required className="hac-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={app.website ? `you@${(app.website || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/.*$/, "")}` : "you@yourcompany.com"} data-testid="claim-email" />
          <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? `E-mails op het domein van ${app.website} worden automatisch goedgekeurd.` : `Emails on the domain of ${app.website} are auto-approved.`}</p>
        </div>
        <div>
          <label className="overline block mb-1 text-[#64748B]">{isNl ? "Je rol" : "Your role"}</label>
          <select className="hac-input" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid="claim-relationship">
            <option value="owner">{isNl ? "Eigenaar / Founder" : "Owner / Founder"}</option>
            <option value="marketing">{isNl ? "Marketing / Communicatie" : "Marketing / Communications"}</option>
            <option value="product">{isNl ? "Product / Engineering" : "Product / Engineering"}</option>
            <option value="other">{isNl ? "Anders" : "Other"}</option>
          </select>
        </div>
        <div>
          <label className="overline block mb-1 text-[#64748B]">{isNl ? "Korte toelichting (optioneel)" : "Short note (optional)"}</label>
          <textarea className="hac-input min-h-[80px]" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={isNl ? "Bv: ik ben de mede-oprichter en wil onze listing up-to-date houden." : "E.g. I'm the co-founder and want to keep our listing up to date."} maxLength={500} data-testid="claim-note" />
        </div>
        {state.error && <p className="text-sm text-red-600">{state.error}</p>}
        <button type="submit" disabled={state.submitting} className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-7 py-3 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors" data-testid="claim-submit">
          {state.submitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {isNl ? "Stuur verificatie" : "Send verification"}
        </button>
      </form>
    </main>
  );
};

export default ClaimAppPage;
